import { MouseEvent } from "react";
import { Image } from "../ImageWrapper/interface";
import { FeatureAttribution } from "../../shared/interfaces/featureAttribution";

export enum CardVariantsEnum {
    TEXT_WITH_LINK = "text_with_link",
    TEXT_WITH_LINK_AND_ICON = "text_with_link_and_icon",
    MEDIA = "media",
    VERTICAL_TO_HORIZONTAL = "vertical_to_horizontal",
    MULTI_MEDIA = "multi_media",
}

export enum VthVariants {
    VERTICAL = "Vertical",
    HORIZONTAL = "Horizontal",
    TALL_VERTICAL = "TallVertical",
}
export type VthVariant =
    | VthVariants.VERTICAL
    | VthVariants.HORIZONTAL
    | VthVariants.TALL_VERTICAL;
export interface ResponsiveVthOptions {
    mobile: VthVariant;
    tablet: VthVariant;
    desktop: VthVariant;
}

export interface ContextualOverride {
    headline?: string;
    flag?: string;
    contextualLineupSlug?: string;
}

/**
 * This type is meant to define the "ContentItem" data that goes into the cards.
 * For now it was moved in part from CardCQW CardData that is now an extension of this.
 * Ultimately data for content items should be normalized and delivered as part of a
 * consistent data type that represents "content"
 */
export interface TransformedCardData {
    //these come from transformAllContentItems
    id: string;
    url: string;
    departments?: {
        sectionList?: (string | undefined)[];
        sectionLabels?: (string | undefined)[];
    };
    category?: string;
    description?: string;
    external?: boolean;
    flag?: string;
    flags?: {
        status?: string;
        label?: string;
    };
    image?: {
        url: string;
        srcset?: string[];
        alt?: string;
        type?: string;
        mass?: string;
        loading?: string;
        aspects?: {
            ratio9x16?: {
                url: string;
            };
            ratio16x9?: {
                url: string;
            };
            ratio1x1?: {
                url: string;
            };
        };
    };
    media?: {
        duration?: number;
    };
    publishTime?: number;
    updateTime?: number;
    sourceId?: string;
    source?: string;
    slug?: string;
    title?: string;
    itemType?: string;
    show?: string;
    author?: {
        name: string;
        image?: string;
    };
    displayComments?: boolean;
    videoId?: string;
    videoCardOverlayType?: string;
    videoDuration?: number;
    videoAirDate?: number;
    videoHasCaptions?: boolean;
    videoStreamType?: string;
    videoTitle?: string;
    flagId?: string;
    headlineId?: string;
    descriptionId?: string;
    metadataId?: string;
    baseSection?: string;
    subSection?: string;
    contextualOverrides?: ContextualOverride[];
}

/**
 * This interface defines the basic card props to be used only by the base Card
 * component stories/Components/Card/index.tsx.  It should include properties that
 * apply to all/most cards and relates to general card functionality
 *
 */
export interface BaseCardProps {
    item: TransformedCardData;
    focus?: boolean;
    renderAsButton?: boolean;
    featureAttribution?: FeatureAttribution;
    variant:
        | CardVariantsEnum.VERTICAL_TO_HORIZONTAL
        | CardVariantsEnum.MEDIA
        | CardVariantsEnum.TEXT_WITH_LINK
        | CardVariantsEnum.TEXT_WITH_LINK_AND_ICON
        | CardVariantsEnum.MULTI_MEDIA;
    cardRef?:
        | React.RefObject<HTMLAnchorElement>
        | React.LegacyRef<HTMLButtonElement>;
    onClick?: (
        e?: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined
    ) => void;
    columns?: number;
}

export interface CardWrapperProps extends BaseCardProps {
    children: JSX.Element;
}

/**
 * These interfaces below define properties that relate to a specific card variant
 * that help define or trigger specific design / layout elements of that card.
 */
export interface SimpleCardProps extends BaseCardProps {
    iconPosition?: "center" | "lowerLeft";
    showIcon?: boolean;
    aspectRatio?: number;
    imageProps?: Image;
}

export interface MediaCardProps extends BaseCardProps {
    aspectRatio?: number;
    showIcon?: boolean;
    displayDuration?: boolean;
}
export interface VerticalToHorizontalCardProps extends BaseCardProps {
    vthBreakpoints?: ResponsiveVthOptions;
    containerClasses?: string;
    aspectRatio?: number;
    showIcon?: boolean;
    showDescription?: boolean;
}

export interface MultiMediaCardProps extends BaseCardProps {
    type?: "image" | "video" | "live";
    aspectRatio?: number;
    showClosedCaptionsIcon?: boolean;
}
